import {useIsFocused, useNavigation} from '@react-navigation/native';
import {Formik} from 'formik';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Alert, Platform, Text, TouchableOpacity, View, useWindowDimensions, KeyboardAvoidingView} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {SafeAreaView, useSafeAreaInsets} from 'react-native-safe-area-context';
import * as yup from 'yup';
import {PyxiLabel} from '~assets/icons';
import ModernButton from '~components/ModernButton';
import ModernTextInput from '~components/ModernTextInput';
import ModernCard from '~components/ModernCard/ModernCard';
import {FadeIn} from '~components/MicroInteractions';

import {NavigationProps} from '~types/navigation/navigation.type';
import {spacing, borderRadius, typography, shadows} from '~constants/design';
import {logScreenView} from '~Utils/firebaseAnalytics';
import {LanguagesModal} from '~components/ModalWithItems/LanguagesModal';
import {useLanguage} from '~containers/Core/navigation/SettingsStack/zustand';
import {useTheme} from '~contexts/ThemeContext';
import {useForgotPassword} from '~hooks/auth/useForgotPassword';
import {SCREENS} from '~constants';

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .email('Enter a valid email')
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Enter a valid email')
    .required('Email is required'),
});

const ForgotPasswordView = () => {
  const {colors} = useTheme();
  const {t} = useTranslation();
  const {width, height} = useWindowDimensions();
  const navigation = useNavigation<NavigationProps>();
  const {bottom} = useSafeAreaInsets();
  const [isLoading, setIsLoading] = useState(false);
  const [isLanguagesModalVisible, setIsLanguagesModalVisible] = useState(false);
  const {mutateAsync: forgotPasswordMutation} = useForgotPassword();
  const isFocused = useIsFocused();
  const {language} = useLanguage();

  const isAndroid = Platform.OS === 'android';
  const paddingBottom = isAndroid ? bottom + spacing.lg : bottom;

  useEffect(() => {
    setIsLoading(false);
  }, [isFocused]);

  useEffect(() => {
    logScreenView('Forgot Password', 'ForgotPassword');
  }, []);

  const styles = {
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    keyboardContainer: {
      flex: 1,
    },
    scrollContainer: {
      flexGrow: 1,
    },
    heroSection: {
      height: height * 0.3,
      position: 'relative' as const,
      overflow: 'hidden' as const,
    },
    heroGradient: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
    },
    decorativeCircle1: {
      position: 'absolute' as const,
      width: width * 0.8,
      height: width * 0.8,
      borderRadius: width * 0.4,
      backgroundColor: colors.textInverse + '10',
      top: -width * 0.3,
      right: -width * 0.3,
    },
    decorativeCircle2: {
      position: 'absolute' as const,
      width: width * 0.6,
      height: width * 0.6,
      borderRadius: width * 0.3,
      backgroundColor: colors.textInverse + '15',
      bottom: -width * 0.2,
      left: -width * 0.2,
    },
    decorativeCircle3: {
      position: 'absolute' as const,
      width: width * 0.4,
      height: width * 0.4,
      borderRadius: width * 0.2,
      backgroundColor: colors.textInverse + '20',
      top: height * 0.1,
      left: width * 0.1,
    },
    languageButton: {
      position: 'absolute' as const,
      top: spacing.xl,
      right: spacing.lg,
      backgroundColor: colors.textInverse + '20',
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: borderRadius.md,
      zIndex: 10,
    },
    languageText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.semibold,
      color: colors.textInverse,
    },
    heroContent: {
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      paddingHorizontal: spacing.xl,
      zIndex: 5,
      height: height * 0.3,
    },
    logoContainer: {
      marginBottom: spacing.md,
    },
    taglineText: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      color: colors.infoDark,
      textAlign: 'center' as const,
      marginTop: spacing.md,
      opacity: 0.95,
      letterSpacing: 0.5,
    },
    floatingElements: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    floatingDot: {
      position: 'absolute' as const,
      width: 8,
      height: 8,
      borderRadius: 4,
      top: 40,
      left: 60,
    },
    formSection: {
      flex: 1,
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.xl,
      paddingBottom: spacing.lg,
    },
    formTitle: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      textAlign: 'center' as const,
      marginBottom: spacing.sm,
      letterSpacing: -0.3,
    },
    formSubtitle: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      color: colors.textSecondary,
      textAlign: 'center' as const,
      marginBottom: spacing.lg,
      opacity: 0.85,
      letterSpacing: 0.1,
      lineHeight: typography.fontSize.base * 1.4,
    },
    inputContainer: {
      marginBottom: spacing.md,
    },
    backToLoginContainer: {
      alignItems: 'center' as const,
      marginTop: spacing.md,
    },
    backToLoginText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      color: colors.primary,
    },
    bottomSection: {
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.lg,
      paddingBottom: Math.max(paddingBottom, spacing.lg),
      alignItems: 'center' as const,
      marginTop: spacing.md,
    },
    loginContainer: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
    },
    loginText: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      fontWeight: typography.fontWeight.normal,
    },
    loginButton: {
      marginLeft: spacing.xs,
    },
    loginButtonText: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.primary,
    },
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <KeyboardAvoidingView
        style={styles.keyboardContainer}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}>
        <KeyboardAwareScrollView
          style={styles.keyboardContainer}
          contentContainerStyle={styles.scrollContainer}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          enableOnAndroid={true}
          enableAutomaticScroll={true}
          keyboardOpeningTime={0}
          extraScrollHeight={Platform.OS === 'ios' ? 20 : 40}
          extraHeight={Platform.OS === 'ios' ? 20 : 40}>
          {/* Hero Section */}
          <FadeIn duration={300}>
            <View style={styles.heroSection}>
              {/* Modern Gradient Background */}
              <LinearGradient
                colors={[colors.primary, '#FFB347', '#FFD700', '#FF7F50', colors.primaryLight]}
                start={{x: 0, y: 0}}
                end={{x: 1, y: 1}}
                style={styles.heroGradient}>
                {/* Decorative Elements */}
                <View style={styles.decorativeCircle1} />
                <View style={styles.decorativeCircle2} />
                <View style={styles.decorativeCircle3} />
              </LinearGradient>

              <TouchableOpacity style={styles.languageButton} onPress={() => setIsLanguagesModalVisible(true)}>
                <Text style={styles.languageText}>{language.toUpperCase()}</Text>
              </TouchableOpacity>

              <View style={styles.heroContent}>
                <FadeIn delay={100} duration={400}>
                  <View style={styles.logoContainer}>
                    <PyxiLabel />
                  </View>
                </FadeIn>

                <FadeIn delay={150} duration={400}>
                  <Text style={styles.taglineText}>{t('signin.discover_connect_experience')}</Text>
                </FadeIn>

                {/* Modern Floating Elements */}
                <View style={styles.floatingElements}>
                  <View style={[styles.floatingDot, {backgroundColor: colors.textInverse + '30'}]} />
                  <View
                    style={[styles.floatingDot, {backgroundColor: colors.textInverse + '20', top: 60, right: 40}]}
                  />
                  <View
                    style={[styles.floatingDot, {backgroundColor: colors.textInverse + '25', bottom: 80, left: 30}]}
                  />
                </View>
              </View>
            </View>
          </FadeIn>

          {/* Form Section */}
          <View style={styles.formSection}>
            <FadeIn delay={50} duration={300}>
              <ModernCard variant="default" padding="xl" style={{marginBottom: spacing.lg, ...shadows.none}}>
                <Text style={styles.formTitle}>{t('signin.forgot_pwd_header')}</Text>
                <Text style={styles.formSubtitle}>{t('signin.forgot_pwd_msg')}</Text>

                <Formik
                  initialValues={{email: ''}}
                  onSubmit={async values => {
                    setIsLoading(true);
                    try {
                      await forgotPasswordMutation({email: values.email.trim()});
                      Alert.alert(
                        t('auth.reset_email_sent') || 'Reset Email Sent',
                        t('auth.reset_email_message') ||
                          'Please check your email for the OTP code to reset your password.',
                        [
                          {
                            text: t('generic.ok') || 'OK',
                            onPress: () => {
                              navigation.navigate(SCREENS.RESET_PASSWORD, {
                                email: values.email.trim(),
                              });
                            },
                          },
                        ],
                      );
                    } catch (error: any) {
                      Alert.alert(
                        t('generic.error') || 'Error',
                        error.message || 'Failed to send password reset email. Please try again.',
                      );
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  validationSchema={validationSchema}
                  validateOnChange={true}
                  validateOnBlur={true}>
                  {formikProps => (
                    <>
                      <View style={styles.inputContainer}>
                        <ModernTextInput
                          label={t('signin.email')}
                          placeholder="Enter your email"
                          value={formikProps.values.email}
                          onChangeText={value => {
                            formikProps.handleChange('email')(value.trim());
                          }}
                          errorText={
                            formikProps.touched.email && formikProps.errors.email
                              ? String(formikProps.errors.email)
                              : undefined
                          }
                          keyboardType="email-address"
                          autoCapitalize="none"
                          autoCorrect={false}
                          variant="outlined"
                          size="lg"
                        />
                      </View>

                      <ModernButton
                        onPress={() => formikProps.handleSubmit()}
                        title={t('signin.send_message') || 'Send Recovery Email'}
                        variant="primary"
                        size="lg"
                        fullWidth
                        loading={isLoading}
                        disabled={!formikProps.isValid || isLoading || !formikProps.values.email.trim()}
                        hapticFeedback
                      />
                    </>
                  )}
                </Formik>
              </ModernCard>
            </FadeIn>
          </View>

          {/* Bottom Section */}
          <FadeIn delay={100} duration={300}>
            <View style={styles.bottomSection}>
              <View style={styles.loginContainer}>
                <Text style={styles.loginText}>{t('auth.remember_password') || 'Remember your password?'}</Text>
                <TouchableOpacity
                  style={styles.loginButton}
                  onPress={() => {
                    navigation.goBack();
                  }}>
                  <Text style={styles.loginButtonText}>{t('signin.login')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </FadeIn>
        </KeyboardAwareScrollView>
      </KeyboardAvoidingView>

      <LanguagesModal isVisible={isLanguagesModalVisible} onClose={() => setIsLanguagesModalVisible(false)} />
    </SafeAreaView>
  );
};

export default ForgotPasswordView;
